/**
 * Trip capture.
 *
 * Field order follows what the driver knows at the kerbside: who, from, to,
 * then the numbers. Distance is fetched automatically once both addresses have
 * coordinates, and stays editable — an override is logged as an amendment.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Combobox, type ComboboxOption } from '@/components/Combobox';
import { Callout, Card, Field, Select, TextArea, TextInput } from '@/components/ui';
import { combineDateTime, formatDate, nowIso, timeInputValue, todayIso } from '@/domain/dates';
import { formatEur, formatKm, parseAmountToCents, parseKmToMeters } from '@/domain/money';
import type { Address, Order, Passenger, PaymentMethod, Trip, TripType } from '@/domain/types';
import { vatRateForTrip } from '@/domain/vat';
import { tripRepo, unlinkedOrders } from '@/db/repositories';
import {
  GeoapifyNotConfiguredError,
  autocompleteAddress,
  geocodeAddress,
  routeDistance,
  type GeoapifyConfig,
  type GeocodeResult,
} from '@/geo/geoapify';
import { useSession } from '@/session/SessionContext';
import {
  passengerDisplayName,
  suggestAddressesForPassenger,
  suggestPassengers,
} from '@/suggest';
import { useT } from '@/i18n';
import {
  createTrip,
  ensureShift,
  resolveOrCreateAddress,
  resolveOrCreatePassenger,
  updateTrip,
} from './tripService';

/** Milliseconds of silence before an online address lookup fires. */
const GEOCODE_DEBOUNCE_MS = 400;

interface AddressDraft {
  id?: string;
  text: string;
  lat?: number;
  lon?: number;
  geocoded: boolean;
}

export function TripFormScreen() {
  const session = useSession();
  const t = useT();
  const navigate = useNavigate();
  const { tripId } = useParams<{ tripId: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(tripId);

  const [existing, setExisting] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [date, setDate] = useState(todayIso());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const [passengerText, setPassengerText] = useState('');
  const [passengerId, setPassengerId] = useState<string | undefined>();

  const [from, setFrom] = useState<AddressDraft>({ text: '', geocoded: false });
  const [to, setTo] = useState<AddressDraft>({ text: '', geocoded: false });

  const [tripType, setTripType] = useState<TripType>('tariffahrt');
  const [distanceText, setDistanceText] = useState('');
  const [computedMeters, setComputedMeters] = useState<number | undefined>();
  const [distanceBusy, setDistanceBusy] = useState(false);
  const [distanceNote, setDistanceNote] = useState<string | null>(null);

  const [fareText, setFareText] = useState('');
  const [tipText, setTipText] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('rechnung');
  const [comment, setComment] = useState('');
  const [vehicleId, setVehicleId] = useState(session.app.defaultVehicleId ?? '');
  const [orderId, setOrderId] = useState(searchParams.get('auftrag') ?? '');
  const [orders, setOrders] = useState<Order[]>([]);
  const [amendmentReason, setAmendmentReason] = useState('');

  const [onlineFrom, setOnlineFrom] = useState<GeocodeResult[]>([]);
  const [onlineTo, setOnlineTo] = useState<GeocodeResult[]>([]);
  const [geoBusy, setGeoBusy] = useState<'from' | 'to' | null>(null);

  const geoConfig = useMemo<GeoapifyConfig | null>(
    () =>
      session.app.geoapifyApiKey
        ? {
            apiKey: session.app.geoapifyApiKey,
            biasLat: session.app.geocodeBiasLat,
            biasLon: session.app.geocodeBiasLon,
            countryCode: session.app.geocodeCountryCode,
          }
        : null,
    [session.app],
  );

  // -- load ----------------------------------------------------------------

  useEffect(() => {
    void (async () => {
      setOrders(await unlinkedOrders(session.dataKey));
    })();
  }, [session.dataKey]);

  // Arriving from the order book with `?auftrag=…`: carry over what was taken
  // down on the phone. The driver can still change any of it — the order stays
  // the record of what was booked, the trip becomes the record of what was
  // driven, and the two are allowed to differ.
  useEffect(() => {
    if (isEdit || !orderId) return;
    const order = orders.find((candidate) => candidate.id === orderId);
    if (!order) return;

    setPassengerText((current) => current || (order.ordererName ?? ''));
    setFrom((current) =>
      current.text ? current : { text: order.pickupLocation, geocoded: false },
    );
    setTo((current) =>
      current.text || !order.destination ? current : { text: order.destination, geocoded: false },
    );
  }, [isEdit, orderId, orders]);

  useEffect(() => {
    if (!tripId) {
      const now = new Date();
      setStartTime(timeInputValue(now.toISOString()));
      setEndTime(timeInputValue(now.toISOString()));
      if (!vehicleId && session.vehicles[0]) setVehicleId(session.vehicles[0].id);
      return;
    }

    void (async () => {
      const trip = await tripRepo.get(session.dataKey, tripId);
      if (!trip) {
        setError(t.trips.notFound);
        setLoading(false);
        return;
      }
      const passenger = session.passengers.find((p) => p.id === trip.passengerId);
      const fromAddress = session.addresses.find((a) => a.id === trip.fromAddressId);
      const toAddress = session.addresses.find((a) => a.id === trip.toAddressId);

      setExisting(trip);
      setDate(trip.date);
      setStartTime(timeInputValue(trip.startedAt));
      setEndTime(timeInputValue(trip.endedAt));
      setPassengerId(trip.passengerId);
      setPassengerText(passenger ? passengerDisplayName(passenger) : '');
      setFrom({
        id: trip.fromAddressId,
        text: fromAddress?.label ?? '',
        lat: fromAddress?.lat,
        lon: fromAddress?.lon,
        geocoded: Boolean(fromAddress?.geocodedAt),
      });
      setTo({
        id: trip.toAddressId,
        text: toAddress?.label ?? '',
        lat: toAddress?.lat,
        lon: toAddress?.lon,
        geocoded: Boolean(toAddress?.geocodedAt),
      });
      setTripType(trip.tripType);
      setDistanceText(formatKm(trip.distanceMeters));
      setComputedMeters(trip.computedDistanceMeters);
      setFareText((trip.fareGrossCents / 100).toFixed(2).replace('.', ','));
      setTipText(trip.tipCents ? (trip.tipCents / 100).toFixed(2).replace('.', ',') : '');
      setPaymentMethod(trip.paymentMethod);
      setComment(trip.comment ?? '');
      setVehicleId(trip.vehicleId);
      setOrderId(trip.orderId ?? '');
      setLoading(false);
    })();
  }, [
    tripId,
    session.dataKey,
    session.passengers,
    session.addresses,
    session.vehicles,
    vehicleId,
    t,
  ]);

  // -- suggestions ---------------------------------------------------------

  const passengerOptions = useMemo<Array<ComboboxOption<Passenger>>>(
    () =>
      suggestPassengers(session.passengers, passengerText).map((suggestion) => ({
        key: suggestion.passenger.id,
        value: suggestion.passenger,
        label: passengerDisplayName(suggestion.passenger),
        detail:
          suggestion.passenger.phone ??
          (session.destinations.tripCountFor(suggestion.passenger.id) > 0
            ? t.trips.tripCount(session.destinations.tripCountFor(suggestion.passenger.id))
            : undefined),
      })),
    [session.passengers, session.destinations, passengerText, t],
  );

  const habitualOrigins = passengerId ? session.destinations.likelyOrigins(passengerId) : [];
  const habitualDestinations = passengerId
    ? session.destinations.likelyDestinations(passengerId)
    : [];

  function addressOptions(
    text: string,
    habitual: readonly string[],
    online: readonly GeocodeResult[],
  ): Array<ComboboxOption<Address | GeocodeResult>> {
    const local = suggestAddressesForPassenger(session.addresses, text, habitual).map(
      (suggestion): ComboboxOption<Address | GeocodeResult> => ({
        key: `local:${suggestion.address.id}`,
        value: suggestion.address,
        label: suggestion.address.label,
        detail: [suggestion.address.postalCode, suggestion.address.city]
          .filter(Boolean)
          .join(' '),
        badge:
          suggestion.reason === 'passenger' ? (
            <span className="badge bg-accent-soft text-accent">{t.trips.habitual}</span>
          ) : undefined,
      }),
    );

    const knownLabels = new Set(local.map((option) => option.label.toLowerCase()));
    const remote = online
      .filter((result) => !knownLabels.has(result.formatted.toLowerCase()))
      .map(
        (result): ComboboxOption<Address | GeocodeResult> => ({
          key: `geo:${result.placeId ?? result.formatted}`,
          value: result,
          label: result.formatted,
          detail: 'Geoapify',
        }),
      );

    return [...local, ...remote];
  }

  // -- online geocoding ----------------------------------------------------

  const fromAbort = useRef<AbortController | null>(null);
  const toAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!geoConfig || from.id || from.text.trim().length < 3) {
      setOnlineFrom([]);
      return;
    }
    const timer = window.setTimeout(() => {
      fromAbort.current?.abort();
      const controller = new AbortController();
      fromAbort.current = controller;
      setGeoBusy('from');
      autocompleteAddress(geoConfig, from.text, controller.signal)
        .then(setOnlineFrom)
        .catch(() => setOnlineFrom([]))
        .finally(() => setGeoBusy((current) => (current === 'from' ? null : current)));
    }, GEOCODE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [from.text, from.id, geoConfig]);

  useEffect(() => {
    if (!geoConfig || to.id || to.text.trim().length < 3) {
      setOnlineTo([]);
      return;
    }
    const timer = window.setTimeout(() => {
      toAbort.current?.abort();
      const controller = new AbortController();
      toAbort.current = controller;
      setGeoBusy('to');
      autocompleteAddress(geoConfig, to.text, controller.signal)
        .then(setOnlineTo)
        .catch(() => setOnlineTo([]))
        .finally(() => setGeoBusy((current) => (current === 'to' ? null : current)));
    }, GEOCODE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [to.text, to.id, geoConfig]);

  // -- distance ------------------------------------------------------------

  const distanceMeters = parseKmToMeters(distanceText) ?? 0;
  const distanceOverridden =
    computedMeters !== undefined && Math.abs(distanceMeters - computedMeters) > 50;

  async function computeDistance() {
    if (!geoConfig) {
      setDistanceNote(t.trips.errNoGeoKey);
      return;
    }
    setDistanceBusy(true);
    setDistanceNote(null);
    try {
      let origin = from.lat !== undefined && from.lon !== undefined ? { lat: from.lat, lon: from.lon } : null;
      let destination = to.lat !== undefined && to.lon !== undefined ? { lat: to.lat, lon: to.lon } : null;

      if (!origin && from.text.trim()) {
        const geocoded = await geocodeAddress(geoConfig, from.text);
        if (geocoded) {
          origin = { lat: geocoded.lat, lon: geocoded.lon };
          setFrom((current) => ({ ...current, lat: geocoded.lat, lon: geocoded.lon, geocoded: true }));
        }
      }
      if (!destination && to.text.trim()) {
        const geocoded = await geocodeAddress(geoConfig, to.text);
        if (geocoded) {
          destination = { lat: geocoded.lat, lon: geocoded.lon };
          setTo((current) => ({ ...current, lat: geocoded.lat, lon: geocoded.lon, geocoded: true }));
        }
      }

      if (!origin || !destination) {
        setDistanceNote(t.trips.errNotLocated);
        return;
      }

      const route = await routeDistance(geoConfig, origin, destination);
      setComputedMeters(route.distanceMeters);
      setDistanceText(formatKm(route.distanceMeters));
      setDistanceNote(
        t.trips.computed(formatKm(route.distanceMeters), Math.round(route.durationSeconds / 60)),
      );
    } catch (cause) {
      if (cause instanceof GeoapifyNotConfiguredError) {
        setDistanceNote(cause.message);
      } else {
        setDistanceNote(
          cause instanceof Error
            ? `${cause.message} ${t.trips.addManually}`
            : t.trips.computeFailed,
        );
      }
    } finally {
      setDistanceBusy(false);
    }
  }

  // Fire automatically once both ends are known and the field is still empty.
  useEffect(() => {
    if (isEdit) return;
    if (distanceText.trim() !== '') return;
    if (from.lat === undefined || to.lat === undefined) return;
    void computeDistance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from.lat, from.lon, to.lat, to.lon]);

  // -- VAT -----------------------------------------------------------------

  const vat = vatRateForTrip(session.company, distanceMeters);
  const fareGrossCents = parseAmountToCents(fareText) ?? 0;
  const tipCents = parseAmountToCents(tipText) ?? 0;

  // -- save ----------------------------------------------------------------

  async function handleSave(closeAfter: boolean) {
    setError(null);

    if (!passengerText.trim()) return setError(t.trips.errPassenger);
    if (!from.text.trim()) return setError(t.trips.errFrom);
    if (!to.text.trim()) return setError(t.trips.errTo);
    if (!vehicleId) return setError(t.trips.errVehicle);
    if (distanceMeters <= 0) return setError(t.trips.errDistance);
    if (existing?.lockedAt && !amendmentReason.trim()) {
      return setError(t.trips.errAmendment);
    }

    setSaving(true);
    try {
      const passenger = await resolveOrCreatePassenger(session.audit, session.passengers, {
        id: passengerId,
        rawName: passengerText,
      });

      const fromAddress = await resolveOrCreateAddress(session.audit, session.addresses, {
        id: from.id,
        label: from.text.trim(),
        lat: from.lat,
        lon: from.lon,
        geocoded: from.geocoded,
      });

      const toAddress = await resolveOrCreateAddress(session.audit, session.addresses, {
        id: to.id,
        label: to.text.trim(),
        lat: to.lat,
        lon: to.lon,
        geocoded: to.geocoded,
      });

      const startedAt = combineDateTime(date, startTime || '00:00');
      const endedAt = combineDateTime(date, endTime || startTime || '00:00');

      const distanceSource: Trip['distanceSource'] =
        session.app.distanceSystemOfRecord === 'odometer'
          ? 'odometer'
          : distanceOverridden || computedMeters === undefined
            ? 'manual'
            : 'geoapify';

      if (existing) {
        await updateTrip(
          session.audit,
          existing.id,
          {
            date,
            startedAt,
            endedAt,
            passengerId: passenger.id,
            fromAddressId: fromAddress.id,
            toAddressId: toAddress.id,
            tripType,
            distanceMeters,
            computedDistanceMeters: computedMeters,
            distanceOverridden,
            distanceSource,
            fareGrossCents,
            vatRatePercent: vat.ratePercent,
            paymentMethod,
            tipCents,
            comment: comment.trim() || undefined,
            vehicleId,
          },
          amendmentReason.trim() || undefined,
        );
      } else {
        const shift = await ensureShift(session.audit, {
          date,
          driverId: session.account.id,
          vehicleId,
        });

        await createTrip(session.audit, {
          shiftId: shift.id,
          driverId: session.account.id,
          vehicleId,
          orderId: orderId || undefined,
          date,
          startedAt,
          endedAt,
          passengerId: passenger.id,
          fromAddressId: fromAddress.id,
          toAddressId: toAddress.id,
          tripType,
          distanceMeters,
          computedDistanceMeters: computedMeters,
          distanceOverridden,
          distanceSource,
          fareGrossCents,
          vatRatePercent: vat.ratePercent,
          paymentMethod,
          tipCents,
          comment: comment.trim() || undefined,
        });
      }

      await session.refreshMasterData();

      if (closeAfter) {
        navigate('/fahrten');
      } else {
        resetForNextTrip();
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.common.saveFailed);
    } finally {
      setSaving(false);
    }
  }

  /** Keep date and vehicle, clear the rest — the shape of a busy shift. */
  function resetForNextTrip() {
    setPassengerText('');
    setPassengerId(undefined);
    setFrom({ text: '', geocoded: false });
    setTo({ text: '', geocoded: false });
    setDistanceText('');
    setComputedMeters(undefined);
    setDistanceNote(null);
    setFareText('');
    setTipText('');
    setComment('');
    setOrderId('');
    const now = new Date().toISOString();
    setStartTime(timeInputValue(now));
    setEndTime(timeInputValue(now));
  }

  if (loading) return <p className="text-muted text-sm">{t.common.loading}</p>;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-heading text-xl font-semibold">
          {isEdit ? t.trips.editTitle : t.trips.new}
        </h1>
        {existing?.lockedAt && (
          <p className="mt-1 text-xs text-amber-700">
            {t.trips.lockedNotice(formatDate(existing.lockedAt.slice(0, 10)))}
          </p>
        )}
      </div>

      <Card title={t.trips.sectionRoute}>
        <div className="space-y-4">
          <Field label={t.trips.passenger} required hint={t.trips.passengerHint}>
            <Combobox
              value={passengerText}
              onChange={(text) => {
                setPassengerText(text);
                setPassengerId(undefined);
              }}
              onSelect={(option) => {
                setPassengerText(passengerDisplayName(option.value));
                setPassengerId(option.value.id);

                // Pre-fill the habitual destination, but only when the history
                // is strong enough that a correction is unlikely.
                const likely = session.destinations.confidentDestination(option.value.id);
                if (likely && !to.text) {
                  const address = session.addresses.find((a) => a.id === likely);
                  if (address) {
                    setTo({
                      id: address.id,
                      text: address.label,
                      lat: address.lat,
                      lon: address.lon,
                      geocoded: Boolean(address.geocodedAt),
                    });
                  }
                }
              }}
              options={passengerOptions}
              autoFocus={!isEdit}
            />
          </Field>

          <Field label={t.trips.fromLabel} required>
            <Combobox
              value={from.text}
              onChange={(text) => setFrom({ text, geocoded: false })}
              onSelect={(option) => setFrom(toDraft(option.value))}
              options={addressOptions(from.text, habitualOrigins, onlineFrom)}
              loading={geoBusy === 'from'}
            />
          </Field>

          <Field label={t.trips.toLabel} required>
            <Combobox
              value={to.text}
              onChange={(text) => setTo({ text, geocoded: false })}
              onSelect={(option) => setTo(toDraft(option.value))}
              options={addressOptions(to.text, habitualDestinations, onlineTo)}
              loading={geoBusy === 'to'}
            />
          </Field>

          <div className="grid grid-cols-[1fr_auto] items-end gap-2">
            <Field
              label={t.trips.distance}
              required
              hint={distanceNote ?? (distanceOverridden ? t.trips.distanceOverridden : undefined)}
            >
              <TextInput
                value={distanceText}
                onChange={(event) => setDistanceText(event.target.value)}
                inputMode="decimal"
                placeholder="23,7"
              />
            </Field>
            <button
              type="button"
              className="btn-secondary mb-[1.375rem]"
              onClick={() => void computeDistance()}
              disabled={distanceBusy || !geoConfig}
            >
              {distanceBusy ? t.trips.computing : t.trips.compute}
            </button>
          </div>

          {!geoConfig && <Callout tone="info">{t.trips.noGeoKey}</Callout>}
        </div>
      </Card>

      <Card title={t.trips.sectionTimes}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.trips.date} required>
            <TextInput type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </Field>
          <Field label={t.trips.vehicle} required>
            <Select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
              <option value="">{t.trips.choose}</option>
              {session.vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.vehicleCode} · {vehicle.licensePlate}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t.trips.startTime} required>
            <TextInput
              type="time"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
            />
          </Field>
          <Field label={t.trips.endTime} required>
            <TextInput
              type="time"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card title={t.trips.sectionBilling}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.trips.fareGross} required>
            <TextInput
              value={fareText}
              onChange={(event) => setFareText(event.target.value)}
              inputMode="decimal"
              placeholder="89,00"
            />
          </Field>
          <Field label={t.trips.tip} hint={t.trips.tipHint}>
            <TextInput
              value={tipText}
              onChange={(event) => setTipText(event.target.value)}
              inputMode="decimal"
              placeholder="0,00"
            />
          </Field>
          <Field label={t.trips.paymentMethod} required>
            <Select
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value as PaymentMethod)}
            >
              {Object.entries(t.labels.payment).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t.trips.tripType} required hint={t.trips.tripTypeHint}>
            <Select
              value={tripType}
              onChange={(event) => setTripType(event.target.value as TripType)}
            >
              {Object.entries(t.labels.tripType).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="bg-subtle mt-4 rounded-xl px-3 py-2.5 text-xs">
          <p className="text-body">
            {t.trips.vatLine(vat.ratePercent, vat.legalBasis)}
            {fareGrossCents > 0 &&
              t.trips.vatOf(
                formatEur(
                  Math.round((fareGrossCents * vat.ratePercent) / (100 + vat.ratePercent)),
                ),
              )}
          </p>
          <p className="text-muted mt-1">{vat.rationale}</p>
        </div>
      </Card>

      <Card title={t.trips.sectionOrder}>
        <div className="space-y-4">
          {orders.length > 0 && !isEdit && (
            <Field label={t.trips.linkedOrder} hint={t.trips.linkedOrderHint}>
              <Select value={orderId} onChange={(event) => setOrderId(event.target.value)}>
                <option value="">{t.trips.noOrder}</option>
                {orders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {t.orders.number(order.sequenceNumber)} ·{' '}
                    {formatDate(order.receivedAt.slice(0, 10))} ·{' '}
                    {order.ordererName ?? t.common.dash} · {order.pickupLocation}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label={t.trips.comment}>
            <TextArea
              rows={3}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={t.trips.commentPlaceholder}
            />
          </Field>

          {existing?.lockedAt && (
            <Field label={t.trips.amendmentReason} required hint={t.trips.amendmentHint}>
              <TextInput
                value={amendmentReason}
                onChange={(event) => setAmendmentReason(event.target.value)}
                placeholder={t.trips.amendmentPlaceholder}
              />
            </Field>
          )}
        </div>
      </Card>

      {error && <Callout tone="danger">{error}</Callout>}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary"
          onClick={() => void handleSave(true)}
          disabled={saving}
        >
          {saving ? t.common.saving : t.common.save}
        </button>
        {!isEdit && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void handleSave(false)}
            disabled={saving}
          >
            {t.trips.saveAndNext}
          </button>
        )}
        <button type="button" className="btn-ghost" onClick={() => navigate(-1)}>
          {t.common.cancel}
        </button>
      </div>
    </div>
  );
}

function toDraft(value: Address | GeocodeResult): AddressDraft {
  if ('formatted' in value) {
    return {
      text: value.formatted,
      lat: value.lat,
      lon: value.lon,
      geocoded: true,
    };
  }
  return {
    id: value.id,
    text: value.label,
    lat: value.lat,
    lon: value.lon,
    geocoded: Boolean(value.geocodedAt),
  };
}

export { nowIso };
